<template>
    <v-breadcrumbs class="pa-0 mb-3 flex-wrap" :items="items" divider=">" color="primary" />
</template>

<script lang="ts">
    import {Component, Prop, Vue, Setup} from 'vue-facing-decorator'
    import {Company} from '~/utils/types/types'
    type LinkPropsType = {
        title: string
        disabled?: boolean
        to?: {
            name: string
            params?: {[key: string]: string | number}
            query?: {[key: string]: string | number}
            hash?: string
        }
        exact?: boolean
        nuxt?: boolean
    }

    @Component({})
    export class Breadcrumbs extends Vue {
        @Prop() text
        @Prop() productType: string
        @Prop() company
        @Prop({default: false, type: Boolean}) isAgency

        @Setup(() => useLocalized()) localized: Function

        routeCompany = {
            type: '',
            name: '',
            id: '',
        }

        get isB2C() {
            return authStore.isB2C
        }

        get homeItem() {
            if (this.$conf.public.mainPageUrl) {
                return {
                    title: this.$t('home'),
                    href: this.$conf.public.mainPageUrl,
                    link: true,
                }
            }
            return {
                title: this.$t('home'),
                to: {name: 'home'},
                exact: true,
            }
        }

        async mounted() {
            const {query} = useRoute()
            if (typeof query['company'] !== 'undefined') {
                const id = query.company
                const rq = {id, shortResponse: false}
                try {
                    this.routeCompany = (await this.$api.companies.get(rq)).companies[0]
                } catch (e) {
                    this.routeCompany = {type: '', name: '', id: ''}
                }
            }
        }

        get items(): LinkPropsType[] {
            const items: LinkPropsType[] = [this.homeItem]
            const language = this.company?.language ? this.company?.language : 'en'
            const route = useRoute()
            switch (route.name) {
                case 'register':
                case 'b2b-register':
                    items.push({
                        title: this.$t('registration'),
                        disabled: true,
                    })
                    break
                case 'orders':
                    items.push({
                        title: this.$t('orders'),
                        disabled: true,
                    })
                    break
                case 'user-settings':
                    items.push({
                        title: this.$t('my_profile'),
                        disabled: true,
                    })
                    break
                case 'order-details':
                    items.push({
                        title: this.$t('orders'),
                        disabled: false,
                        to: {name: 'orders'},
                        exact: true,
                    })
                    items.push({
                        title: this.$t('order_details'),
                        disabled: true,
                    })
                    break
                case 'company-profile':
                    items.push({
                        title: this.$t('my_company'),
                        disabled: true,
                    })
                    break
                case 'company-client-profile':
                    if (this.company?.type === Company.TourAgency || this.company?.type === Company.TourOperator) {
                        items.push({
                            title: this.$t('agencies_and_distributors'),
                            to: {name: 'companyAgencies'},
                        })
                    }
                    if (this.company?.type === Company.Corporator) {
                        items.push({
                            title: this.$t('corporate_clients'),
                            to: {name: 'companyCorporateClients'},
                        })
                    }
                    if (this.company?.type === Company.HubSupplier || this.company?.type === Company.InternalSupplier) {
                        items.push({
                            title: this.$t('suppliers', 2),
                            to: {name: 'companySuppliers'},
                        })
                    }
                    if (+route.params.id === 0) {
                        items.push({
                            title: this.$t('create_new_company'),
                            disabled: true,
                        })
                    } else if (this.company?.name) {
                        // TODO [P1] - fix others this?.company.name
                        items.push({
                            title: this.localized(this.company.name, language) || this.$t('edit_company'),
                            disabled: true,
                        })
                    }
                    break
                case 'companyPersons':
                    items.push({
                        title: this.$t('my_company'),
                        disabled: true,
                    })
                    break
                case 'allPersons':
                    items.push({
                        title: this.$t('all_users'),
                        disabled: true,
                    })
                    break
                case 'registrationRequests':
                    items.push({
                        title: this.$t('registration_requests'),
                        disabled: true,
                    })
                    break
                case 'allCompanies':
                    items.push({
                        title: this.$t('all_companies'),
                        disabled: true,
                    })
                    break
                case 'companyPerson':
                    if (
                        this.company?.type === Company.TourAgency ||
                        this.company?.type === Company.TourOperator ||
                        this.routeCompany?.type === Company.TourAgency ||
                        this.routeCompany?.type === Company.TourOperator
                    ) {
                        items.push({
                            title: this.$t('agencies_and_distributors'),
                            to: {name: 'companyAgencies'},
                        })
                    }
                    if (this.company?.type === Company.Corporator || this.routeCompany?.type === Company.Corporator) {
                        items.push({
                            title: this.$t('corporate_clients'),
                            to: {name: 'companyCorporateClients'},
                        })
                    }
                    if (this.routeCompany?.name) {
                        items.push({
                            title:
                                this.routeCompany.name[language] || this.routeCompany?.name || this.$t('edit_company'),
                            to:
                                authStore.person.companyId === this.routeCompany.id
                                    ? {name: 'company-profile', params: {id: this.routeCompany.id}}
                                    : {name: 'company-client-profile', params: {id: this.routeCompany.id}},
                        })
                    } else if (this.company?.name) {
                        items.push({
                            title: this.company.name[language] || this.company?.name || this.$t('edit_company'),
                            to:
                                authStore.person.companyId === this.company.id
                                    ? {name: 'company-profile', params: {id: this.company.id}}
                                    : {name: 'company-client-profile', params: {id: this.company.id}},
                        })
                    }
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'companyClients':
                    items.push({
                        title: this.$t('private_clients'),
                        disabled: true,
                    })
                    break
                case 'companyClient':
                    items.push({
                        title: this.$t('private_clients'),
                        disabled: false,
                        to: {name: 'companyClients'},
                        exact: true,
                    })
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'companyCorporateClients':
                    items.push({
                        title: this.$t('corporate_clients'),
                        disabled: true,
                    })
                    break
                case 'companyAgencies':
                    items.push({
                        title: this.$t('agencies_and_distributors'),
                        disabled: true,
                    })
                    break
                case 'members':
                    items.push({
                        title: this.membersText,
                        disabled: true,
                    })
                    break
                case 'member':
                    items.push({
                        title: this.membersText,
                        disabled: false,
                        to: {name: 'members'},
                        exact: true,
                    })
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'infoPage':
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'reports':
                    items.push({
                        title: this.$t('reports'),
                        disabled: true,
                    })
                    break
                case 'supplierProducts':
                    items.push({
                        title: this.$t('products'),
                        disabled: true,
                    })
                    break
                case 'supplierContract':
                    // For hotels (ownHotel/accommodation), skip "Products" and go directly to "Hotels"
                    // since "Products" and "Hotels" would link to the same page
                    if (this.productType !== 'ownHotel' && this.productType !== 'accommodation') {
                        items.push({
                            title: this.$t('products'),
                            disabled: false,
                            to: {name: 'supplierProducts', hash: `#${this.productType}`},
                        })
                    }
                    items.push({
                        title: this.productType ? this.$t(`productTypeBreadcrumbs.${this.productType}`) : '...',
                        disabled: false,
                        to: {name: 'supplierProducts', hash: `#${this.productType}`},
                    })
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'hotelDescription':
                    items.push({
                        title: this.$t('products'),
                        disabled: false,
                        to: {name: 'supplierProducts'},
                        exact: true,
                    })
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'activityDescription':
                    items.push(
                        {
                            title: this.$t('products'),
                            disabled: false,
                            to: {name: 'supplierProducts'},
                            exact: true,
                        },
                        {
                            title: this.$t('activities.activities'),
                            disabled: false,
                            to: {name: 'supplierProducts', hash: '#activities'},
                            exact: true,
                        },
                        {
                            title: this.text,
                            disabled: true,
                        }
                    )
                    break
                case 'companySuppliers':
                    items.push({
                        title: this.$t('suppliers', 2),
                        disabled: true,
                    })
                    break
                case 'companySupplier':
                    items.push({
                        title: this.$t('suppliers', 2),
                        disabled: false,
                        to: {name: 'companySuppliers'},
                    })
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'supplierPerson':
                    items.push({
                        title: this.$t('suppliers', 2),
                        disabled: false,
                        to: {name: 'companySuppliers'},
                    })
                    if (this.routeCompany?.name) {
                        items.push({
                            title:
                                this.routeCompany.name[language] || this.routeCompany?.name || this.$t('edit_company'),
                            to: {name: 'companySupplier', params: {id: this.routeCompany.id}},
                        })
                    } else if (this.company?.name) {
                        items.push({
                            title: this.company.name,
                            disabled: false,
                            to: {name: 'companySupplier', params: {id: this.company.id}},
                        })
                    }
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'favorites':
                    items.push({
                        title: this.$t('favorites'),
                        disabled: true,
                    })
                    break
                case 'payments':
                    items.push({
                        title: this.$t('payments'),
                        disabled: true,
                    })
                    break
                case 'inboundEmails':
                    items.push({
                        title: this.$t('inbound_emails'),
                        disabled: true,
                    })
                    break
                case 'kpis':
                    items.push({
                        title: this.$t('KPI', 2),
                        disabled: true,
                    })
                    break
                case 'kpi':
                    items.push({
                        title: this.$t('KPI', 2),
                        disabled: false,
                        to: {name: 'kpis'},
                    })
                    items.push({
                        title: this.text,
                        disabled: true,
                    })
                    break
                case 'businessIntelligence':
                    items.push({
                        title: this.$t('business_intelligence'),
                        disabled: true,
                    })
                    break
                case 'feedback':
                    items.push({
                        title: this.$t('reviews_and_feedback'),
                        disabled: true,
                    })
                    break
                case 'dashboards':
                    items.push({
                        title: this.$t('dashboards'),
                        disabled: true,
                    })
                    break
                case 'campaigns':
                    items.push({
                        title: this.$t('campaigns'),
                        disabled: true,
                    })
                    break
                case 'campaign-details':
                    items.push({
                        title: this.$t('campaigns'),
                        disabled: false,
                        to: {name: 'campaigns'},
                        exact: true,
                    })
                    if (+route.params.id === 0) {
                        items.push({
                            title: this.$t('create_new_campaign'),
                            disabled: true,
                        })
                    } else {
                        items.push({
                            title: this.$t('campaign_details'),
                            disabled: true,
                        })
                    }
                    break
                case 'locations':
                    items.push({
                        title: this.$t('locations'),
                        disabled: true,
                    })
                    break
                case 'venues':
                    items.push({
                        title: this.$t('venues'),
                        disabled: true,
                    })
                    break
                case 'vehicleTypes':
                    items.push({
                        title: this.$t('vehicle_types'),
                        disabled: true,
                    })
                    break
                case 'vehicleType':
                    items.push({
                        title: this.$t('vehicle_types'),
                        disabled: false,
                        to: {name: 'vehicleTypes'},
                    })
                    items.push({
                        title: this.text || '...',
                        disabled: true,
                    })
                    break
                case 'exchangeRates':
                    items.push({
                        title: this.$t('exchange_rates'),
                        disabled: true,
                    })
                    break
                case 'salesSettings':
                    items.push({
                        title: this.$t('sales_settings'),
                        disabled: true,
                    })
                    break
                case 'communications':
                    items.push({
                        title: this.$t('communications_and_notes'),
                        disabled: true,
                    })
                    break
                case 'paymentsSettings':
                    items.push({
                        title: this.$t('payments_settings'),
                        disabled: true,
                    })
                    break
                case 'onlinePayments':
                    items.push({
                        title: this.$t('online_payments'),
                        disabled: true,
                    })
                    break
            }
            return items
        }

        get membersText() {
            return this.$t('members')
        }
    }

    export default Breadcrumbs
</script>
